/**
 * Holiday calculation utilities for frontend
 * Matches backend HolidayService logic
 */

interface Holiday {
  date: Date
  name: string
}

/**
 * Calculate Easter Sunday for a given year using Computus algorithm
 */
function calculateEaster(year: number): Date {
  const a = year % 19
  const b = Math.floor(year / 100)
  const c = year % 100
  const d = Math.floor(b / 4)
  const e = b % 4
  const f = Math.floor((b + 8) / 25)
  const g = Math.floor((b - f + 1) / 3)
  const h = (19 * a + b - d - g + 15) % 30
  const i = Math.floor(c / 4)
  const k = c % 4
  const l = (32 + 2 * e + 2 * i - h - k) % 7
  const m = Math.floor((a + 11 * h + 22 * l) / 451)
  const month = Math.floor((h + l - 7 * m + 114) / 31)
  const day = ((h + l - 7 * m + 114) % 31) + 1

  return new Date(year, month - 1, day)
}

/**
 * Calculate Thanksgiving (4th Thursday of November) for a given year
 */
function calculateThanksgiving(year: number): Date {
  // First day of November
  const nov1 = new Date(year, 10, 1) // Month 10 = November (0-indexed)
  // Day of week: 0 = Sunday, 1 = Monday, ..., 4 = Thursday
  const firstDayOfWeek = nov1.getDay()
  // Find first Thursday
  const daysToFirstThursday = firstDayOfWeek <= 4 
    ? (4 - firstDayOfWeek) 
    : (11 - firstDayOfWeek)
  const firstThursday = new Date(year, 10, 1 + daysToFirstThursday)
  // Add 3 weeks for 4th Thursday
  firstThursday.setDate(firstThursday.getDate() + 21)
  return firstThursday
}

/**
 * Get all Catholic holidays for a given year
 */
export function getHolidaysForYear(year: number): Holiday[] {
  const holidays: Holiday[] = []

  // New Year's Day
  holidays.push({ date: new Date(year, 0, 1), name: "New Year's Day" })

  // Easter and Easter Monday
  const easter = calculateEaster(year)
  holidays.push({ date: easter, name: 'Easter' })
  const easterMonday = new Date(easter)
  easterMonday.setDate(easterMonday.getDate() + 1)
  holidays.push({ date: easterMonday, name: 'Easter Monday' })

  // Thanksgiving
  holidays.push({ date: calculateThanksgiving(year), name: 'Thanksgiving' })

  // Christmas
  holidays.push({ date: new Date(year, 11, 25), name: 'Christmas' })

  return holidays.sort((a, b) => a.date.getTime() - b.date.getTime())
}

/**
 * Days skipped for a holiday (spec: "the entire WEEK of - or after the said holiday, if it's on
 * the weekend"). Weeks run Sunday-Saturday.
 * - Mon-Fri holiday: that Sun-Sat week.
 * - Sunday holiday (e.g. Easter): the Sun-Sat week starting that Sunday.
 * - Saturday holiday: the holiday itself plus the following Sun-Sat week.
 * Must match backend app/services/holidays.py get_holiday_week.
 */
export function getHolidayWeek(holidayDate: Date): { start: Date; end: Date } {
  const date = new Date(holidayDate)
  date.setHours(0, 0, 0, 0)
  const dow = date.getDay() // 0 = Sunday ... 6 = Saturday
  const start = new Date(date)
  if (dow !== 6) start.setDate(date.getDate() - dow)
  const end = new Date(start)
  end.setDate(start.getDate() + (dow === 6 ? 7 : 6))
  end.setHours(23, 59, 59, 999)
  return { start, end }
}

/**
 * Check if a date falls within a holiday week
 */
export function isInHolidayWeek(checkDate: Date, holidays: Holiday[]): { isHoliday: boolean; holidayName?: string } {
  for (const holiday of holidays) {
    const week = getHolidayWeek(holiday.date)
    if (checkDate >= week.start && checkDate <= week.end) {
      return { isHoliday: true, holidayName: holiday.name }
    }
  }
  return { isHoliday: false }
}

